import { Input } from "antd";
import { CSSProperties, FC, useCallback, useEffect, useMemo, useState } from "react";
import styles from './inputSearch.module.scss'
import { debounce } from "lodash";
import { SearchIcon } from "../../libs/corporate/icons/outlined/action/MainCollection";
import { GridFilterAction } from "../../system/hooks/useGridFilter";

export type typeFilter = "paramFilter" | "columnFilter" | "expression"
interface IInputSearchProps {
    id?: string;
    searchValue: string;
    placeholder: string;

    typeFilter: typeFilter;
    gridFilter: IGridFilter;
    dispatchGridFilter: (value: GridFilterAction) => void;
    filterName: string; // название фильтра для paramFilter
    operator?: OperatorType;

    handlePressEnter?: (value: string) => void;
    handleSearchChange?: (value: string) => void;

    minSearchLength?: number;
    debounceDelay?: number;
    style?: CSSProperties;
}

const InputSearch: FC<IInputSearchProps> = ({
    id,
    searchValue, // значение фильтра
    placeholder,
    typeFilter, // тип фильтра (paramFilter, expression)
    gridFilter,
    dispatchGridFilter,
    filterName,  // для paramFilter-название, для expression - шаблон выражения
    operator = 'Eq',
    handlePressEnter,
    handleSearchChange, //для возврата значения поиска
    minSearchLength = 3,
    debounceDelay = 1250,
}) => {
    const [internalValue, setInternalValue] = useState<string>(searchValue);

    //поиск по columnFilter
    const handleColumnFilterChange = useCallback((value: string) => {
        let normalizedValue = value === undefined ? null : value;

        if(operator === 'Like' && value !== '') normalizedValue = `%${normalizedValue}%`
        
        if (value === '') {
            dispatchGridFilter({
                type: "deleteColumnFilter",
                payload: filterName
            });
        }

        if (normalizedValue !== null && normalizedValue !== '') {
            dispatchGridFilter({
                type: "addColumnFilter",
                payload: {
                    name: filterName,
                    value: normalizedValue,
                    operator: operator,
                    invisible: false
                } as IGridColumnFilter
            });
        }
    }, [dispatchGridFilter]);

    //поиск по expression
    const handleExpressionFilterChange = useCallback((value: string) => {
        if (value.length >= minSearchLength) {
            const searchExpression = buildSearchExpression(value, filterName);
            dispatchGridFilter({ type: "addCustomWhereExpressionFilter", payload: searchExpression });
        } else if (value.length === 0) {
            dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
        }
    }, [dispatchGridFilter]);

    const buildSearchExpression = (searchValue: string, baseExpression: string) => {
        if (!searchValue.trim()) return "";

        return baseExpression
            .replace(/%SEARCH_VALUE%/g, searchValue)
    };

    //поиск по paramFilter
    const handleParamFilterChange = useCallback((value: string) => {
        handleSearchChange?.(value);
        if (value === '') {
            dispatchGridFilter({
                type: "deleteSpecificParamFilter",
                payload: filterName
            });
        }

        if (value.trim() !== '') {
            if (gridFilter?.paramFilter) {
                dispatchGridFilter({
                    type: "addParamFilter",
                    payload: {
                        [filterName]: value
                    }
                });
            } else {
                dispatchGridFilter({
                    type: "paramSimpleFilter",
                    payload: {
                        gridColumnFilter: gridFilter.columnFilters,
                        gridParamFilter: {
                            [filterName]: value
                        }
                    }
                });
            }
        }
    }, [dispatchGridFilter, gridFilter.columnFilters, gridFilter.paramFilter]);

    const executeSearch = useCallback((value: string) => {
        if (value.length >= minSearchLength || value.length === 0) {
            handleSearchChange?.(value);
            
            if (typeFilter === "columnFilter") {
                handleColumnFilterChange(value);
            }
            if (typeFilter === "expression") {
                handleExpressionFilterChange(value);
            }
            if (typeFilter === "paramFilter") {
                handleParamFilterChange(value);
            }
        }
    }, [typeFilter, handleColumnFilterChange, handleExpressionFilterChange, handleParamFilterChange, handleSearchChange, minSearchLength]);

    const debouncedSearch = useMemo(
        () => debounce((value: string) => {
            executeSearch(value);
        }, debounceDelay),
        [executeSearch, debounceDelay]
    );

    useEffect(() => {
        return () => debouncedSearch.cancel();
    }, [debouncedSearch]);

    useEffect(() => {
        setInternalValue(searchValue);
    }, [searchValue]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInternalValue(value);
        if (value === '') {
            debouncedSearch.cancel();
            executeSearch('');
        } else {
            debouncedSearch(value);
        }
    };

    const handleInputPressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            debouncedSearch.cancel();
            const value = (e.target as HTMLInputElement).value;

            executeSearch(value);
            handlePressEnter?.(value);
        }
    };

    return (
        <div className={styles.filtersWrapper}>
            <Input
                className="custom-input-withPrefix"
                id={id}
                style={{
                    width: '100%',
                    ...(internalValue && internalValue.trim() ? {
                        boxShadow: '0px 0px 0px 3px #99ccff'
                    } : {})
                }}
                value={internalValue}
                placeholder={placeholder}
                prefix={<SearchIcon />}
                onKeyDown={handleInputPressEnter}
                onPressEnter={handleInputPressEnter}
                onChange={handleInputChange}
                allowClear
            />
        </div>
    )
}

export default InputSearch